import { FEATURE_TOUR_IDS, TEST_IDS, Z_INDEX } from '@va/constants';
import { websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { DropdownIcon, GlobeIcon, HelpIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { isUserLoggedInWithCustomUrl } from '@va/standalone/shared/helpers';
import { UserRole } from '@va/types/website';
import { HorizontalDivider } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import LanguageSelector from '../languageSelector/LanguageSelector';
import { UserInitials } from './UserInitials';
import { UserMenuButton } from './UserMenuButton';
import { useUserInformation } from './useUserInformation';
import { useUserMenu } from './useUserMenu';

type OnCloseCallback = () => void;
type UserMenuProps = {
  renderUserInfo?: (onClose: OnCloseCallback) => ReactNode;
  renderAccountSettingsButton?: (onClose: OnCloseCallback) => ReactNode;
  logoutButton?: ReactNode;
  notificationsCount?: number;
};

export function UserMenu({
  renderUserInfo,
  renderAccountSettingsButton,
  logoutButton,
  notificationsCount,
}: UserMenuProps) {
  const translate = useTranslate();
  const { userInitials } = useUserInformation();
  const { isExpanded, toggleExpanded, closeMenu } = useUserMenu();
  const [isLanguageSelectorVisible, setIsLanguageSelectorVisible] = useState(false);
  const websiteRole = useSelector(websiteRoleSelectors.selectWebsiteRole);

  const handleLanguageSelectorToggle = () => setIsLanguageSelectorVisible((prev) => !prev);
  return (
    <TooltipWrapper
      open={isExpanded}
      onOpenChange={(open) => toggleExpanded(open)}
      trigger='click'
      zIndex={Z_INDEX.userMenu}
      tooltipClassNames='shadow-md rounded-24'
      content={
        <div className='min-w-[25rem] rounded-24 bg-white border-2 border-gray-mercury text-black px-3 py-3'>
          {!isLanguageSelectorVisible ? (
            <>
              {renderUserInfo && renderUserInfo(closeMenu)}

              <UserMenuButton
                leftIcon={<GlobeIcon color='var(--color-black)' />}
                text={translate('locale.language')}
                rightIcon={<DropdownIcon />}
                onClick={handleLanguageSelectorToggle}
              />
              <HorizontalDivider />

              {renderAccountSettingsButton && renderAccountSettingsButton(closeMenu)}

              {(!isUserLoggedInWithCustomUrl() || websiteRole !== UserRole.WATCHER) && (
                <a href={translate('supportCenter.rootPage')} target='_blank' rel='noreferrer' onClick={closeMenu}>
                  <UserMenuButton leftIcon={<HelpIcon />} text={translate('button.helpCenter')} />
                </a>
              )}
              {logoutButton}
            </>
          ) : (
            <LanguageSelector
              onBackClick={handleLanguageSelectorToggle}
              onLanguageSelected={() => {
                handleLanguageSelectorToggle();
                closeMenu();
              }}
            />
          )}
        </div>
      }
      arrow={false}
      useDefaultStyle={false}
    >
      <div
        data-testid={TEST_IDS.specific.accountMenuTrigger}
        data-tour={FEATURE_TOUR_IDS.userMenu}
        className={classNames(
          'mr-2 sm-initial:mr-0 sm-initial:h-60px rounded-15 bg-primary-lighter-06 hover:bg-primary-lighter-13 sm-initial:p-3 flex justify-between items-center cursor-pointer',
          { isOpen: isExpanded },
        )}
      >
        <UserInitials initials={userInitials} notificationsCount={notificationsCount} />
        <DropdownArrow open={isExpanded} className='hidden sm-initial:block sm-initial:ml-2' />
      </div>
    </TooltipWrapper>
  );
}
